import {FC, useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {t} from 'i18next';
import {FormProvider, useForm} from 'react-hook-form';
import {teamMemberInformationFormValidationSchema} from './../validation-schemas';
import {ITeamMemberInfoFormData, TeamMemberStatus} from './../models';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  setTeamMember,
  setProjectList,
  setClientInfo,
} from '../../../../redux/slices/teamMemberSlice';
import AurUserFormFields from '../../../user-management/aur-user-form-fields/AurUserFormFields';
import AurDialog from '../../../common/aur-dialog/AurDialog';
import {getUserFindByEmail} from '../../../../api/UserManagementService';
import {
  setUserRolesData,
} from '../../../../redux/slices/userRoleSlice';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {useNavigate} from 'react-router-dom';
import {UserRoleIds} from '../../../../types/user-management/userManagementTypes';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

type TeamMemberInfoProps = {
  activeStep: number;
  setActiveStep: (value: number) => void;
  isSysAdmin?: boolean;
  onSysAdmin: (checked: boolean) => void;
};

const TeamMemberInfo: FC<TeamMemberInfoProps> = (props) => {
  const {activeStep, setActiveStep} = props;
  const {trackGAEvent} = useAnalytics();

  const [showAdminPopup, setShowAdminPopup] = useState(false);
  const [showExistingAdminPopup, setShowExistingAdminPopup] = useState(false);
  const dispatch = useDispatch();
  const [isFetchUserRolesLoading, setIsFetchUserRolesLoading] = useState(false);

  const {teamMemberInfo: teamMemberData} = useSelector(
    (state: RootState) => state.teamMember
  );

  const [teamMemberFormData, setTeamMemberFormData] = useState({
    ipAddressList: teamMemberData.ipAddressList,
    isLimitIp: teamMemberData.isLimitIp,
    isSysAdmin: props.isSysAdmin,
  });

  const navigate = useNavigate();
  const backNavigation = () => {
    navigate('/users');
  };

  const methods = useForm({
    mode: 'all',
    defaultValues: teamMemberData,
    resolver: yupResolver(teamMemberInformationFormValidationSchema()),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: {isValid, errors},
    control,
  } = methods;

const checkExistingUser = async (email: string): Promise<boolean> => {
  setIsFetchUserRolesLoading(true);
  try {
    const response = await getUserFindByEmail(email);
    // Existing user
    return Boolean(response?.data?.data?.userId);
  } catch (error: any) {
    const status = error?.response?.status;
    // Backend uses 400/404 when the user does not exist.
    // Treat these as "new user" and allow the normal creation flow.
    if (status === 400 || status === 404) {
      return false;
    }
    // Unexpected API/server error should NOT silently create a user.
    console.error('Error checking existing user:', error);
    throw error;
  } finally {
    setIsFetchUserRolesLoading(false);
  }
};

  const onSubmit = async (submittedFormData: ITeamMemberInfoFormData) => {
    try {
      const userExists = await checkExistingUser(submittedFormData.email);
      if (userExists) {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
          subPage: {
            subpage_name: t('gaTracking.users.existingTeamMember'),
            subpage_type: GA_SUBPAGE_TYPE.MODAL,
          },
        });
        setShowExistingAdminPopup(true);
        return;
      }

      // New user - continue with the existing flow.
      dispatch(setUserRolesData([]));
      submittedFormData.status = TeamMemberStatus.invited;
      if (teamMemberFormData.isSysAdmin) {
        submittedFormData.roleId = UserRoleIds.SystemAdmin;
        props.onSysAdmin(true);
      } else {
        submittedFormData.roleId = undefined;
        props.onSysAdmin(false);
      }

      dispatch(setTeamMember(submittedFormData));

      if (teamMemberFormData.isSysAdmin) setActiveStep(activeStep + 2);
      else setActiveStep(activeStep + 1);
    } catch (error) {
      console.error('Failed to check existing user:', error);
    }
  };

  const onAdminCancel = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.users.addingSysAdminCancel'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.shouldThisMemberBeSystemAdmin'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    setTeamMemberFormData({...teamMemberFormData, isSysAdmin: false});
    setShowAdminPopup(false);
  };

  const onExistingAdminCancel = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.goBack'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.users.backTo'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.existingTeamMember'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    setShowExistingAdminPopup(false);
  };

  const onAdminOkay = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.yesIamSure'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.users.addingSysAdminComplete'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.shouldThisMemberBeSystemAdmin'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    const fullData = {
      role: 'System Administrator',
      roleId: UserRoleIds.SystemAdmin,
      client: '',
      clientId: '',
      permissionGroup: '',
      permissionGroupId: '',
      project: '',
      workspaces: [],
    };
    dispatch(setClientInfo(fullData));
    dispatch(setProjectList([]));
    setTeamMemberFormData({...teamMemberFormData, isSysAdmin: true});
    setShowAdminPopup(false);
  };

  const onExistingAdminOkay = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.exitForm'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.users.exitTo'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.existingTeamMember'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    setShowAdminPopup(false);
    backNavigation();
  };

  const onSysAdminCheck = (checked: boolean) => {
    if (checked) {
      // Checked - Show confirmation dialog
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.users.systemAdminCheck'),
          type: GA_CLICK_TYPES.CHECKBOX,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.users.modalUnsavedChanges'),
        },
        subPage: {
          subpage_name: t('gaTracking.users.teamMemberInfo'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
      setShowAdminPopup(true);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.users.shouldThisMemberBeSystemAdmin'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
      return;
    }
    setTeamMemberFormData({...teamMemberFormData, isSysAdmin: false});
    const fullData = {
      role: '',
      roleId: '',
      client: '',
      clientId: '',
      permissionGroup: '',
      permissionGroupId: '',
      project: '',
      workspaces: [],
    };
    dispatch(setClientInfo(fullData));
    dispatch(setProjectList([]));
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          autoComplete="off"
          data-testid="teamMemberForm__form"
          className="team-member-form"
          onSubmit={(..._arguments) =>
            void handleSubmit(onSubmit)(..._arguments)
          }
        >
          <AurUserFormFields
            control={control}
            errors={errors}
            register={register}
            setValue={setValue}
            isValid={isValid}
            buttonVisible={true}
            teamMemberData={teamMemberFormData}
            onSysAdminCheck={onSysAdminCheck}
          />
        </form>
      </FormProvider>
      <AurDialog
        dialogTitle={t('common.justToConfirm')}
        popupTitle={t('editDetails.sysAdminAccessWarning')}
        popupContent={t('editDetails.teamMemberSysAdminAccess')}
        labelActionOk={t('common.iAmSure')}
        labelActionCancel={t('common.cancel')}
        onOk={onAdminOkay}
        onCancel={onAdminCancel}
        showDialog={showAdminPopup}
        buttonStyle="sideBySide"
        className="with-max-width"
      />
      <AurDialog
        popupTitle={t('addEditTeamMember.modal.existingUser.title')}
        popupContent={t('addEditTeamMember.modal.existingUser.content')}
        labelActionOk={t('addEditTeamMember.modal.existingUser.okButtonLabel')}
        labelActionCancel={t(
          'addEditTeamMember.modal.existingUser.cancelButtonLabel'
        )}
        onOk={onExistingAdminOkay}
        onCancel={onExistingAdminCancel}
        showDialog={showExistingAdminPopup}
        buttonStyle="sideBySide"
      />
      {<AurLoadingIndicator overlay={true} isOpen={isFetchUserRolesLoading} />}
    </>
  );
};

export default TeamMemberInfo;
